import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { api } from "../lib/api";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user?.is_admin) {
      setUnread(0);
      return;
    }
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const { data } = await api.get("/admin/comments/unread-count");
        if (!cancelled) setUnread(data.unread || 0);
      } catch {
        /* ignore */
      }
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 30000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [user]);

  if (location.pathname === "/") return null;

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const isPaid = !!user?.has_paid;

  return (
    <nav className="border-b-2 border-black bg-[#F4F4F4] sticky top-0 z-40" data-testid="navbar">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-3 flex items-center justify-between gap-4">
        <Link to={isPaid ? "/dashboard" : "/home"} className="flex items-center gap-2 shrink-0" data-testid="navbar-logo">
          <div className="w-3 h-3 bg-[#E00000]" />
          <span className="font-serif-display text-xl sm:text-2xl text-black" style={{ fontWeight: 800 }}>
            Canadian Opinions
          </span>
        </Link>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
          {/* Language toggle — always visible */}
          <div className="flex items-stretch border-2 border-black" data-testid="nav-lang-toggle">
            <button
              onClick={() => setLang("en")}
              className={`px-2 py-1 text-xs uppercase tracking-widest transition-colors ${lang === "en" ? "bg-black text-white" : "bg-white text-black hover:bg-black hover:text-white"}`}
              style={{ fontWeight: 900 }}
              data-testid="nav-lang-en"
              aria-pressed={lang === "en"}
            >
              EN
            </button>
            <button
              onClick={() => setLang("fr")}
              className={`px-2 py-1 text-xs uppercase tracking-widest border-l-2 border-black transition-colors ${lang === "fr" ? "bg-black text-white" : "bg-white text-black hover:bg-black hover:text-white"}`}
              style={{ fontWeight: 900 }}
              data-testid="nav-lang-fr"
              aria-pressed={lang === "fr"}
            >
              FR
            </button>
          </div>

          {user ? (
            <>
              {isPaid && (
                <>
                  <Link
                    to="/dashboard"
                    className="hidden sm:inline-block text-xs uppercase tracking-widest text-black border-b-2 border-transparent hover:border-black"
                    style={{ fontWeight: 800 }}
                    data-testid="nav-dashboard"
                  >
                    {t("nav_headline")}
                  </Link>
                  <Link
                    to="/vote"
                    className="text-xs uppercase tracking-widest text-black border-b-2 border-transparent hover:border-black"
                    style={{ fontWeight: 800 }}
                    data-testid="nav-vote"
                  >
                    {t("nav_vote")}
                  </Link>
                  <Link
                    to="/results"
                    className="hidden sm:inline-block text-xs uppercase tracking-widest text-black border-b-2 border-transparent hover:border-black"
                    style={{ fontWeight: 800 }}
                    data-testid="nav-results"
                  >
                    {t("nav_results")}
                  </Link>
                  <Link
                    to="/discussion"
                    className="text-xs uppercase tracking-widest text-black border-b-2 border-transparent hover:border-black"
                    style={{ fontWeight: 800 }}
                    data-testid="nav-discussion"
                  >
                    {t("nav_discussion")}
                  </Link>
                </>
              )}
              {!isPaid && (
                <Link
                  to="/paywall"
                  className="text-xs uppercase tracking-widest text-white bg-[#E00000] border-2 border-[#E00000] px-3 py-2"
                  style={{ fontWeight: 900 }}
                  data-testid="nav-unlock"
                >
                  {t("nav_unlock")}
                </Link>
              )}
              {user.is_admin && (
                <Link
                  to="/admin"
                  className="relative text-xs uppercase tracking-widest px-3 py-2 border-2 border-black bg-black text-white hover:bg-white hover:text-black transition-colors"
                  style={{ fontWeight: 900 }}
                  data-testid="nav-admin"
                >
                  {t("nav_admin")}
                  {unread > 0 && (
                    <span
                      className="absolute -top-2 -right-2 bg-[#E00000] text-white text-[10px] leading-none px-1.5 py-1 border-2 border-white"
                      style={{ fontWeight: 900 }}
                      data-testid="nav-admin-badge"
                    >
                      {unread}
                    </span>
                  )}
                </Link>
              )}
              <button
                onClick={handleLogout}
                className="text-xs uppercase tracking-widest px-3 py-2 border-2 border-black bg-white hover:bg-black hover:text-white transition-colors"
                style={{ fontWeight: 900 }}
                data-testid="nav-logout"
              >
                {t("nav_logout")}
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="text-xs uppercase tracking-widest text-black"
                style={{ fontWeight: 800 }}
                data-testid="nav-login"
              >
                {t("nav_login")}
              </Link>
              <Link
                to="/register"
                className="text-xs uppercase tracking-widest px-3 py-2 border-2 border-black bg-black text-white hover:bg-white hover:text-black transition-colors"
                style={{ fontWeight: 900 }}
                data-testid="nav-register"
              >
                {t("nav_register")}
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
